/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { css, jsx } from '@compiled/react';
import { useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import RetryIcon from '@atlaskit/icon/core/retry';
import { Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import VisuallyHidden from '@atlaskit/visually-hidden';

import { messages } from '../../common/messages';

type AimoConfigurationLoadStateProps = {
	errorMessage?: string;
	onRetry?: () => void;
	status: 'failed' | 'loading';
	testId: string;
	variant: 'menu' | 'panel';
};

const loadingStyles = css({
	display: 'grid',
	gap: token('space.100'),
	paddingBlock: token('space.100'),
	paddingInline: token('space.100'),
});

const panelLoadingStyles = css({
	gap: token('space.150'),
	paddingBlock: token('space.100'),
	paddingInline: 0,
});

const loadingRowStyles = css({
	backgroundColor: token('color.skeleton'),
	borderRadius: token('radius.full'),
	height: '12px',
});

const loadingShortRowStyles = css({
	width: '42%',
});

const loadingMediumRowStyles = css({
	width: '68%',
});

const loadingLongRowStyles = css({
	width: '100%',
});

const menuLoadingLongRowStyles = css({
	width: '90%',
});

const menuLoadingShortRowStyles = css({
	width: '46%',
});

const loadingFieldStyles = css({
	backgroundColor: token('color.skeleton'),
	borderRadius: token('radius.small'),
	height: '40px',
});

const loadingTextAreaStyles = css({
	backgroundColor: token('color.skeleton'),
	borderRadius: token('radius.small'),
	height: '84px',
});

const loadErrorStyles = css({
	alignItems: 'center',
	alignContent: 'center',
	display: 'grid',
	gap: token('space.050'),
	justifyItems: 'center',
	minHeight: '112px',
	paddingBlock: token('space.100'),
	paddingInline: token('space.100'),
	textAlign: 'center',
});

const panelLoadErrorStyles = css({
	paddingInline: 0,
});

const AimoConfigurationLoadingState = ({
	testId,
	variant,
}: Pick<AimoConfigurationLoadStateProps, 'testId' | 'variant'>) => {
	const intl = useIntl();

	if (variant === 'panel') {
		return (
			<div css={[loadingStyles, panelLoadingStyles]} data-testid={testId} role="status">
				<VisuallyHidden>{intl.formatMessage(messages.loadingConfiguration)}</VisuallyHidden>
				<div css={[loadingRowStyles, loadingShortRowStyles]} />
				<div css={loadingFieldStyles} />
				<div css={[loadingRowStyles, loadingMediumRowStyles]} />
				<div css={loadingTextAreaStyles} />
				<div css={[loadingRowStyles, loadingLongRowStyles]} />
			</div>
		);
	}

	return (
		<div css={loadingStyles} data-testid={testId} role="status">
			<VisuallyHidden>{intl.formatMessage(messages.loadingConfiguration)}</VisuallyHidden>
			<div css={[loadingRowStyles, menuLoadingLongRowStyles]} />
			<div css={[loadingRowStyles, loadingMediumRowStyles]} />
			<div css={[loadingRowStyles, menuLoadingShortRowStyles]} />
		</div>
	);
};

const AimoConfigurationErrorState = ({
	errorMessage,
	onRetry,
	testId,
	variant,
}: Omit<AimoConfigurationLoadStateProps, 'status'>) => {
	const intl = useIntl();

	return (
		<div
			css={[loadErrorStyles, variant === 'panel' && panelLoadErrorStyles]}
			data-testid={testId}
			role="alert"
		>
			<div>
				<Text color="color.text.danger">
					{errorMessage ?? intl.formatMessage(messages.failedToLoadConfiguration)}
				</Text>
			</div>
			<div>
				<Button
					appearance="default"
					iconBefore={RetryIcon}
					isDisabled={!onRetry}
					onClick={(event) => {
						event.preventDefault();
						event.stopPropagation();
						onRetry?.();
					}}
					onMouseDown={(event) => {
						event.preventDefault();
						event.stopPropagation();
					}}
					onMouseUp={(event) => {
						event.preventDefault();
						event.stopPropagation();
					}}
					spacing="compact"
				>
					{intl.formatMessage(messages.retryConfigurationLoad)}
				</Button>
			</div>
		</div>
	);
};

export const AimoConfigurationLoadState = ({
	errorMessage,
	onRetry,
	status,
	testId,
	variant,
}: AimoConfigurationLoadStateProps): JSX.Element =>
	status === 'loading' ? (
		<AimoConfigurationLoadingState testId={testId} variant={variant} />
	) : (
		<AimoConfigurationErrorState
			errorMessage={errorMessage}
			onRetry={onRetry}
			testId={testId}
			variant={variant}
		/>
	);
